/**
 * The verdict main returns when it closed a pool that had ALREADY OPENED,
 * because a withdrawal was standing at the moment the dial settled.
 *
 * Shared by both layers so they can't drift on the spelling:
 *   • main — `connect-to-database` returns it from the abandoned branch after
 *     `closeConnectionById` (electron/main.ts).
 *   • renderer — `connectToDatabase` tells this apart from a real dial failure.
 *
 * Why the renderer has to tell them apart: withdrawals are keyed on connection
 * id, not on the attempt that asked. So a withdrawal issued by one subsystem —
 * a validation pass aborting when its dialog closes, a teardown loop, a lazy
 * connect being forgotten — cancels whatever dial is in flight for that id,
 * including a dial some OTHER subsystem is waiting on. The joiner is then told
 * "cancelled" for a connection it very much still wants, and every caller in
 * the chain (session restore included) records a failure the user never caused
 * and cannot explain. `Docs/Session_Management.md` covers the intent model.
 */
export const CONNECT_CANCELLED_ERROR = 'Connection cancelled';

/** True when a connect result is main's withdrawn-mid-dial teardown. */
export function isConnectCancelledError(error: string | undefined): boolean {
  return error === CONNECT_CANCELLED_ERROR;
}

/**
 * The verdict main returns when a connect was refused because the connection
 * was DELETED — the delete intent was pending when the connect arrived, or
 * arrived while it was dialing (round 5).
 *
 * Distinct from `CONNECT_CANCELLED_ERROR` on purpose. The renderer retries a
 * plain cancel "with force" when its own intent still stands (another subsystem
 * cancelled the dial), and that retry dialled the deleted connection back to
 * life — a live pool, a tunnel, and the post-connect save re-creating the
 * record. This one is never retried and never reported as a failure: the user
 * asked for the connection to go.
 */
export const CONNECT_REFUSED_DELETED_ERROR = 'Connection deleted';
/** Machine-readable twin of the message, carried as the result's `errorCode`. */
export const CONNECT_REFUSED_DELETED_CODE = 'CONNECT_REFUSED_DELETED';

/** True when a connect result is main's refusal for a deleted connection. */
export function isConnectRefusedByDelete(error: string | undefined, errorCode: string | undefined): boolean {
  return errorCode === CONNECT_REFUSED_DELETED_CODE || error === CONNECT_REFUSED_DELETED_ERROR;
}
